import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { Eye, Trash2, AlertTriangle } from 'lucide-react';

interface AssessmentRow {
  assessment_id: string;
  school_id: string;
  school_name: string;
  teacher_user_id: string | null;
  teacher_name: string | null;
  teacher_email: string | null;
  pupil_id: string | null;
  pupil_first_name: string | null;
  pupil_last_name: string | null;
  writing_type: string | null;
  ai_grade: string | null;
  final_grade: string | null;
  status: string | null;
  extracted_text: string | null;
  justification: string | null;
  created_at: string;
}

interface SchoolRow {
  id: string;
  name: string;
}

interface AttemptRow {
  attempt_id: string;
  created_at: string;
  assessed_by_email: string | null;
  school_id: string | null;
  school_name: string | null;
  pupil_display_name: string;
  year_group: string;
  file_count: number;
  has_pdf: boolean;
  status: 'failed' | 'processing';
  failure_stage: string | null;
  failure_message: string | null;
  error_shown_to_teacher: string | null;
}

type TimelineRow =
  | ({ kind: 'completed' } & AssessmentRow)
  | ({ kind: 'failed' | 'processing' } & AttemptRow);

const PAGE_SIZE = 100;
const ALL_SCHOOLS = '__all__';
const ALL_STATUSES = '__all__';
const ALL_TYPES = '__all__';

export default function AdminAssessments() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [schoolFilter, setSchoolFilter] = useState(ALL_SCHOOLS);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [writingTypeFilter, setWritingTypeFilter] = useState(ALL_TYPES);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [pipelineStatusFilter, setPipelineStatusFilter] = useState<'all' | 'completed' | 'failed' | 'processing'>('all');

  // Debounce search input by 400 ms to avoid a query on every keystroke
  useEffect(() => {
    const t = setTimeout(() => { setSearchQuery(searchInput); setOffset(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // View content dialog
  const [viewRow, setViewRow] = useState<AssessmentRow | null>(null);
  const [viewLogging, setViewLogging] = useState(false);

  // Delete dialog
  const [deleteRow, setDeleteRow] = useState<AssessmentRow | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  const { data: schools = [] } = useQuery({
    queryKey: ['admin-schools-list'],
    queryFn: async () => {
      const { data, error } = await supabase.from('schools').select('id, name').order('name');
      if (error) throw error;
      return data as SchoolRow[];
    },
  });

  const { data: assessments = [], isLoading } = useQuery({
    queryKey: ['admin-assessments', schoolFilter, dateFrom, dateTo, searchQuery, offset],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_admin_assessments', {
        p_school_id: schoolFilter === ALL_SCHOOLS ? null : schoolFilter,
        p_limit: PAGE_SIZE,
        p_offset: offset,
        p_date_from: dateFrom ? new Date(dateFrom).toISOString() : null,
        p_date_to: dateTo ? new Date(dateTo + 'T23:59:59').toISOString() : null,
        p_search: searchQuery.trim() || null,
      });
      if (error) throw error;
      return data as AssessmentRow[];
    },
  });

  const showAttempts = pipelineStatusFilter === 'all' || pipelineStatusFilter === 'failed' || pipelineStatusFilter === 'processing';
  const showCompleted = pipelineStatusFilter === 'all' || pipelineStatusFilter === 'completed';

  const { data: attempts = [], isLoading: attemptsLoading } = useQuery({
    queryKey: ['admin-attempts', schoolFilter, dateFrom, dateTo, pipelineStatusFilter],
    enabled: showAttempts,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_admin_failed_attempts', {
        p_school_id: schoolFilter === ALL_SCHOOLS ? null : schoolFilter,
        p_status: pipelineStatusFilter === 'failed' || pipelineStatusFilter === 'processing'
          ? pipelineStatusFilter
          : null,
        p_limit: PAGE_SIZE,
        p_offset: 0,
        p_date_from: dateFrom ? new Date(dateFrom).toISOString() : null,
        p_date_to: dateTo ? new Date(dateTo + 'T23:59:59').toISOString() : null,
      });
      if (error) throw error;
      return (data ?? []) as AttemptRow[];
    },
  });

  const writingTypes = useMemo(() => {
    const types = new Set(assessments.map(a => a.writing_type).filter(Boolean) as string[]);
    return [...types].sort();
  }, [assessments]);

  const timeline = useMemo((): TimelineRow[] => {
    const completedRows: TimelineRow[] = showCompleted
      ? assessments
          .filter(a => {
            if (statusFilter !== ALL_STATUSES && a.status !== statusFilter) return false;
            if (writingTypeFilter !== ALL_TYPES && a.writing_type !== writingTypeFilter) return false;
            return true;
          })
          .map(a => ({ kind: 'completed' as const, ...a }))
      : [];

    const attemptRows: TimelineRow[] = showAttempts
      ? attempts.map(a => ({ kind: a.status as 'failed' | 'processing', ...a }))
      : [];

    return [...completedRows, ...attemptRows].sort(
      (x, y) => new Date(y.created_at).getTime() - new Date(x.created_at).getTime()
    );
  }, [assessments, attempts, statusFilter, writingTypeFilter, showCompleted, showAttempts]);

  const handleViewContent = async (row: AssessmentRow) => {
    setViewRow(row);
    if (!viewLogging) {
      setViewLogging(true);
      await supabase.rpc('log_admin_action', {
        p_action: 'assessment_content_viewed',
        p_target_type: 'assessment',
        p_target_id: row.assessment_id,
        p_metadata: { school_id: row.school_id, teacher_user_id: row.teacher_user_id },
        p_reason: null,
      });
      setViewLogging(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRow || !deleteReason.trim()) return;
    setDeleting(true);
    const { error } = await supabase
      .from('assessments')
      .delete()
      .eq('id', deleteRow.assessment_id);
    if (error) {
      setDeleting(false);
      toast({ title: 'Delete failed', description: error.message, variant: 'destructive' });
      return;
    }
    await supabase.rpc('log_admin_action', {
      p_action: 'assessment_deleted',
      p_target_type: 'assessment',
      p_target_id: deleteRow.assessment_id,
      p_metadata: { school_id: deleteRow.school_id, teacher_user_id: deleteRow.teacher_user_id },
      p_reason: deleteReason.trim(),
    });
    setDeleting(false);
    setDeleteRow(null);
    setDeleteReason('');
    toast({ title: 'Assessment deleted' });
    queryClient.invalidateQueries({ queryKey: ['admin-assessments'] });
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const gradeBadgeVariant = (grade: string | null) => {
    if (!grade) return 'secondary' as const;
    if (grade === 'GD') return 'default' as const;
    return 'outline' as const;
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-4">
        <div>
          <h1 className="text-2xl font-bold">Assessments</h1>
          <p className="text-sm text-muted-foreground mt-1">Platform-wide assessment records</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <Input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search extracted text, teacher, pupil…"
            className="w-72"
          />

          <Select value={schoolFilter} onValueChange={v => { setSchoolFilter(v); setOffset(0); }}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="All schools" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_SCHOOLS}>All schools</SelectItem>
              {schools.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={pipelineStatusFilter}
            onValueChange={v => {
              setPipelineStatusFilter(v as 'all' | 'completed' | 'failed' | 'processing');
              setOffset(0);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Pipeline status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All pipeline states</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
            </SelectContent>
          </Select>

          {(pipelineStatusFilter === 'all' || pipelineStatusFilter === 'completed') && (
            <Select value={statusFilter} onValueChange={v => setStatusFilter(v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STATUSES}>All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="modified">Modified</SelectItem>
              </SelectContent>
            </Select>
          )}

          {writingTypes.length > 0 && (
            <Select value={writingTypeFilter} onValueChange={v => setWritingTypeFilter(v)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_TYPES}>All types</SelectItem>
                {writingTypes.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Input
            type="date"
            value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); setOffset(0); }}
            className="w-40"
            placeholder="From date"
            title="From date"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={e => { setDateTo(e.target.value); setOffset(0); }}
            className="w-40"
            placeholder="To date"
            title="To date"
          />
        </div>

        {/* Table */}
        {(isLoading || attemptsLoading) ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="rounded-lg border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-4 py-3 font-medium">ID</th>
                  <th className="text-left px-4 py-3 font-medium">School</th>
                  <th className="text-left px-4 py-3 font-medium">Teacher</th>
                  <th className="text-left px-4 py-3 font-medium">Pupil</th>
                  <th className="text-left px-4 py-3 font-medium">Type</th>
                  <th className="text-left px-4 py-3 font-medium">Grade</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {timeline.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No assessments found.</td>
                  </tr>
                ) : (
                  timeline.map(row => {
                    const isAttempt = row.kind === 'failed' || row.kind === 'processing';
                    const isStale = isAttempt
                      && row.kind === 'processing'
                      && new Date(row.created_at).getTime() < Date.now() - 10 * 60 * 1000;
                    const rowId = isAttempt
                      ? (row as AttemptRow).attempt_id
                      : (row as AssessmentRow).assessment_id;

                    return (
                      <tr
                        key={rowId}
                        className={`border-b last:border-0 ${
                          row.kind === 'failed'
                            ? 'bg-destructive/5'
                            : row.kind === 'processing'
                            ? 'bg-amber-50/50'
                            : ''
                        }`}
                      >
                        {/* ID */}
                        <td className="px-4 py-3">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="font-mono text-xs text-muted-foreground cursor-default">
                                  {rowId.slice(0, 8)}…
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="right">
                                <span className="font-mono text-xs select-all">{rowId}</span>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>

                        {/* School */}
                        <td className="px-4 py-3">{row.school_name ?? '—'}</td>

                        {/* Teacher */}
                        <td className="px-4 py-3 text-muted-foreground">
                          {isAttempt
                            ? ((row as AttemptRow).assessed_by_email ?? '—')
                            : ((row as AssessmentRow).teacher_name ?? '—')}
                        </td>

                        {/* Pupil */}
                        <td className="px-4 py-3">
                          {isAttempt
                            ? (row as AttemptRow).pupil_display_name || <span className="text-muted-foreground">—</span>
                            : (() => {
                                const a = row as AssessmentRow;
                                return a.pupil_first_name
                                  ? `${a.pupil_first_name} ${a.pupil_last_name ?? ''}`.trim()
                                  : <span className="text-muted-foreground">—</span>;
                              })()}
                        </td>

                        {/* Type */}
                        <td className="px-4 py-3 text-muted-foreground">
                          {isAttempt ? '—' : ((row as AssessmentRow).writing_type ?? '—')}
                        </td>

                        {/* Grade */}
                        <td className="px-4 py-3">
                          {isAttempt ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (() => {
                            const a = row as AssessmentRow;
                            const grade = a.final_grade ?? a.ai_grade;
                            return <Badge variant={gradeBadgeVariant(grade)}>{grade ?? '—'}</Badge>;
                          })()}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          {row.kind === 'completed' && (
                            <Badge variant="outline" className="text-xs">
                              {(row as AssessmentRow).status ?? '—'}
                            </Badge>
                          )}
                          {row.kind === 'failed' && (
                            <Badge variant="destructive" className="text-xs gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Failed{(row as AttemptRow).failure_stage ? ` (${(row as AttemptRow).failure_stage})` : ''}
                            </Badge>
                          )}
                          {row.kind === 'processing' && (
                            <div className="flex items-center gap-1.5">
                              <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 bg-amber-50">
                                Processing
                              </Badge>
                              {isStale && (
                                <span className="text-xs text-amber-600 font-medium">Stale</span>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Date */}
                        <td className="px-4 py-3 text-muted-foreground">
                          {formatDate(row.created_at)}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3">
                          {!isAttempt ? (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                title="View content"
                                onClick={() => handleViewContent(row as AssessmentRow)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                title="Delete assessment"
                                onClick={() => setDeleteRow(row as AssessmentRow)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (row as AttemptRow).failure_message ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-xs">
                                  <p className="text-xs font-medium mb-1">Error detail</p>
                                  <p className="text-xs text-muted-foreground break-words">
                                    {(row as AttemptRow).failure_message}
                                  </p>
                                  {(row as AttemptRow).error_shown_to_teacher && (
                                    <p className="text-xs mt-1 text-muted-foreground italic">
                                      Teacher saw: {(row as AttemptRow).error_shown_to_teacher}
                                    </p>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex gap-2 items-center text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </Button>
          <span className="text-muted-foreground">
            {`Showing ${timeline.length} result${timeline.length !== 1 ? 's' : ''}`}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={assessments.length < PAGE_SIZE}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* View content dialog */}
      <Dialog open={!!viewRow} onOpenChange={open => { if (!open) setViewRow(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assessment content</DialogTitle>
          </DialogHeader>
          {viewRow && (
            <div className="space-y-4 py-2 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground border-b pb-3">
                <span>School: <strong className="text-foreground">{viewRow.school_name}</strong></span>
                <span>Teacher: <strong className="text-foreground">{viewRow.teacher_name ?? '—'}</strong></span>
                <span>Pupil: <strong className="text-foreground">
                  {viewRow.pupil_first_name ? `${viewRow.pupil_first_name} ${viewRow.pupil_last_name ?? ''}`.trim() : '—'}
                </strong></span>
                <span>Grade: <strong className="text-foreground">{viewRow.final_grade ?? viewRow.ai_grade ?? '—'}</strong></span>
              </div>
              {viewRow.extracted_text && (
                <div>
                  <p className="font-medium mb-1">Extracted text</p>
                  <pre className="whitespace-pre-wrap font-sans text-xs bg-muted rounded p-3">{viewRow.extracted_text}</pre>
                </div>
              )}
              {viewRow.justification && (
                <div>
                  <p className="font-medium mb-1">AI justification</p>
                  <p className="text-muted-foreground">{viewRow.justification}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewRow(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteRow} onOpenChange={open => { if (!open) { setDeleteRow(null); setDeleteReason(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete assessment?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              This permanently deletes the assessment record. The writing sample and pupil record are not affected.
            </p>
            <div className="space-y-1.5">
              <Label>Reason <span className="text-destructive">*</span></Label>
              <Textarea
                placeholder="Required — describe the reason for deletion"
                value={deleteReason}
                onChange={e => setDeleteReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteRow(null); setDeleteReason(''); }}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting || !deleteReason.trim()}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
